import React, { useCallback } from 'react';
import Modal from 'utils/packages/Modal';
import { CALL_API } from 'middleware/api';
import { SettingsItem } from 'design-system/SettingsItem';
import { IconTrash } from 'design-system/icons';
import { IconBoldGallery } from 'design-system/icons/IconBoldGallery';
import { deleteGroupImage, uploadGroupImage } from 'actions';
import { useAppDispatch } from 'store/hooks';
// The same size the app resizes the picture to before uploading it
const IMAGE_SIZE = 300;
const JPEG_QUALITY = 0.9;
const pickImageFile = () => new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => resolve(input.files?.[0] ?? undefined);
    input.oncancel = () => resolve(undefined);
    input.click();
});
/** Crops the image to a square from its centre and scales it, like the app's 'cover' resize. */
const resizeImage = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
        const side = Math.min(image.width, image.height);
        const canvas = document.createElement('canvas');
        canvas.width = IMAGE_SIZE;
        canvas.height = IMAGE_SIZE;
        canvas
            .getContext('2d')
            ?.drawImage(image, (image.width - side) / 2, (image.height - side) / 2, side, side, 0, 0, IMAGE_SIZE, IMAGE_SIZE);
        URL.revokeObjectURL(url);
        canvas.toBlob((blob) => resolve(blob ?? undefined), 'image/jpeg', JPEG_QUALITY);
    };
    image.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(undefined);
    };
    image.src = url;
});
/**
 * Web version of the group picture picker: the browser's file picker replaces
 * the app's camera and photo library sheet.
 */
export const useGroupAvatarPicker = (group) => {
    const dispatch = useAppDispatch();
    const choosePicture = useCallback(async () => {
        const file = await pickImageFile();
        if (!file)
            return;
        const image = await resizeImage(file);
        if (!image)
            return;
        const body = new FormData();
        // The browser's FormData takes a file name; React Native's typing doesn't
        body.append.call(body, 'image', image, 'group.jpg');
        // The browser sets the multipart Content-Type with its boundary itself, so
        // the app's fixed 'multipart/form-data' header is left out
        const action = uploadGroupImage(body, group.id);
        const { headers: _headers, ...callApi } = action[CALL_API];
        dispatch({ ...action, [CALL_API]: callApi });
    }, [dispatch, group.id]);
    const removePicture = useCallback(() => {
        if (group.iconUrl) {
            dispatch(deleteGroupImage(group.id, group.iconUrl));
        }
    }, [dispatch, group.id, group.iconUrl]);
    return useCallback(() => {
        if (!group.iconUrl) {
            choosePicture();
            return;
        }
        Modal.show(<>
        <SettingsItem light icon={<IconBoldGallery gradientKey="skyBlue"/>} title="Choose a picture" onPress={() => Modal.hide(choosePicture)}/>
        <SettingsItem light icon={<IconTrash gradientKey="darkPink"/>} title="Remove" onPress={() => Modal.hide(removePicture)}/>
      </>, { title: 'Select Group Picture' });
    }, [choosePicture, group.iconUrl, removePicture]);
};
